import { Container } from '@mui/material';
import {
  useCheckLogin,
  useGetTherapistSpecializations,
  useUpdateAdmin,
  useUpdateTherapist,
} from '_api';
import {
  Admin,
  AdminPayload,
  ConstantsNameDescriptions,
  SpecializationName,
  Therapist,
} from '_api/types';
import { ACCOUNT_TYPE, AdminRouteMap, US_STATES } from '_constants';
import { SEVERITY } from '_constants/derived';
import { AlertMessage } from 'components/molecules';
import {
  InputField,
  SelectField,
  SelectMultipleField,
} from 'components/molecules/formFields';
import { FormBuilder } from 'components/organisms/forms/form-builder';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigateTo } from 'utils';
import { useHospitals, useUser } from 'utils/hooks';

import { ProfileFormProps } from './types';

const createGeneralFields = (isSelfOrSuperAdmin: boolean) => [
  {
    component: InputField,
    fieldKey: 'firstName',
    label: 'First Name',
    disabled: !isSelfOrSuperAdmin,
    required: false,
    xs: 6,
  },
  {
    component: InputField,
    fieldKey: 'lastName',
    label: 'Last Name',
    disabled: !isSelfOrSuperAdmin,
    required: false,
    xs: 6,
  },
  {
    component: InputField,
    fieldKey: 'email',
    label: 'Email',
    disabled: true,
    required: false,
    xs: 4,
  },
  {
    component: InputField,
    fieldKey: 'phoneNumber',
    label: 'Phone Number',
    disabled: !isSelfOrSuperAdmin,
    required: false,
    mask: '(000) 000-0000',
    type: 'number',
    xs: 4,
  },
];

const createTherapistFormFields = (
  hospitalOptions: ConstantsNameDescriptions<number>[],
  therapistSpecializationOptions:
    | ConstantsNameDescriptions<SpecializationName>[]
    | undefined,
  isSelfOrSuperAdmin: boolean,
) => [
  {
    component: InputField,
    fieldKey: 'phoneExtension',
    label: 'Extension',
    disabled: !isSelfOrSuperAdmin,
    required: false,
    xs: 4,
  },
  {
    component: SelectMultipleField,
    fieldKey: 'therapistSpecializations',
    label: 'Role',
    options: therapistSpecializationOptions,
    required: false,
    xs: 6,
    disabled: !isSelfOrSuperAdmin,
  },
  {
    component: SelectField,
    fieldKey: 'hospitalId',
    label: 'Hospital',
    options: hospitalOptions,
    required: false,
    xs: 6,
    disabled: !isSelfOrSuperAdmin,
  },
  {
    component: InputField,
    fieldKey: 'streetAddress',
    label: 'Street Address',
    required: false,
    xs: 12,
    disabled: !isSelfOrSuperAdmin,
  },
  {
    component: InputField,
    fieldKey: 'city',
    label: 'City',
    required: false,
    xs: 6,
    disabled: !isSelfOrSuperAdmin,
  },
  {
    component: SelectField,
    fieldKey: 'state',
    options: US_STATES.map((state) => ({
      name: state,
      description: state,
    })),
    label: 'State',
    required: false,
    xs: 3,
    disabled: !isSelfOrSuperAdmin,
  },
  {
    component: InputField,
    fieldKey: 'zipCode',
    label: 'Zip Code',
    required: false,
    xs: 3,
    disabled: !isSelfOrSuperAdmin,
  },
];

export const ProfileForm = ({ profile, isLoading }: ProfileFormProps) => {
  const navigate = useNavigateTo();
  const { mutateAsync: updateTherapist } = useUpdateTherapist();
  const { mutateAsync: updateAdmin } = useUpdateAdmin();
  const { data: specializations, isFetched } = useGetTherapistSpecializations();
  const [account, setAccount] = useState(profile);
  const { data: me } = useCheckLogin();
  const { isAdmin } = useUser();
  const { userId } = useParams();
  const { hospitalOptions } = useHospitals();
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const isMe = me && me.id == userId;
  const isSelfOrAdmin = isMe || isAdmin;
  const isTherapistUser = useMemo(
    () => account.accountType === ACCOUNT_TYPE.THERAPIST,
    [account],
  );

  const getPayload = () => {
    const {
      accountType: _accountType,
      fullImageUrl: _fullImageUrl,
      hospital: _hospital,
      status: _status,
      hospitalId,
      therapistSpecializations,
      ...therapistPayload
    } = account as Therapist;

    if (isTherapistUser && checkIfConstantNameDescriptions(therapistSpecializations)) {
      return {
        ...therapistPayload,
        hospitalId: hospitalId || _hospital.id,
        therapistSpecializations: therapistSpecializations.map((x) => x.name),
      };
    } else {
      return {
        ...therapistPayload,
        hospitalId: hospitalId || _hospital.id,
        therapistSpecializations: therapistSpecializations,
      };
    }
  };

  const handleRequestOutcome = (data: Therapist | Admin) => {
    if (data && data.id) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate(AdminRouteMap.USERS);
      }, 2000);
    }
  };

  const onSubmit = () => {
    if (isTherapistUser) {
      updateTherapist(getPayload())
        .then((data) => handleRequestOutcome(data))
        .catch(() => setShowError(true));
    } else {
      updateAdmin(account as AdminPayload)
        .then((data) => handleRequestOutcome(data))
        .catch(() => setShowError(true));
    }
  };

  const checkIfConstantNameDescriptions = (
    objArray: ConstantsNameDescriptions<SpecializationName>[],
  ) => {
    return objArray?.every((obj) => obj.name && obj.description);
  };
  const formUpdate = async (key: any, value: any) => {
    setAccount((current) => ({ ...current, [key]: value }));
  };

  const userRole = useMemo(
    () =>
      isTherapistUser
        ? (profile as Therapist).therapistSpecializations.map((spec) => spec.name)
        : '',
    [profile],
  );

  const therapistSpecializationOptions = useMemo(() => {
    if (isFetched) {
      return specializations;
    } else {
      return [];
    }
  }, [specializations]);

  const formFields = createGeneralFields(isSelfOrAdmin);
  const therapistFormFields = createTherapistFormFields(
    hospitalOptions,
    therapistSpecializationOptions,
    isSelfOrAdmin,
  );

  const initValue = {
    ...profile,
    hospitalId: profile.hospital?.id,
    therapistSpecializations: userRole,
  };

  return !isLoading ? (
    <Container>
      <FormBuilder
        formFields={
          isTherapistUser ? [...formFields, ...therapistFormFields] : formFields
        }
        hideSubmit={false}
        initValues={initValue}
        onSubmit={onSubmit}
        onUpdate={formUpdate}
        wrapperCss={{
          paddingBottom: '50px',
        }}
      />
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Profile successfully updated '}
      />
      <AlertMessage
        open={showError}
        onClose={() => setShowError(false)}
        severity={SEVERITY.error}
        message={'An Error occurred while updating profile. Please Try again later.'}
      />
    </Container>
  ) : null;
};
